<template>
  <div v-if="stations" class="space-y-4">
    <div v-if="!showMore">
      <CVButton v-if="!showMore" variant="link" class="!px-0" @click="showMore = true">
        {{ t('showMore') }}
      </CVButton>
    </div>
    <div v-else class="grid gap-4 md:grid-cols-3">
      <SelectInsuranceCaseProvider
        :value="insuranceCaseProvider"
        @change="handleInsuranceCaseProviderChange"
      />

      <SelectPartner :partner="locationsAndTimes.partner" @change="handlePartnerSelection" />
    </div>
    <div class="flex flex-col items-stretch gap-4 md:flex-row md:items-end">
      <StationAndTimesForm
        :stations="stations"
        :station="locationsAndTimes.station"
        :start-date="locationsAndTimes.startDate"
        :end-date="locationsAndTimes.endDate"
        class="flex-1"
        @change="handleStationAndTimesChange"
      />
      <div v-if="longTermEligible" class="flex h-8 items-center">
        <Checkbox
          class="align-self-end"
          :model-value="locationsAndTimes.longTerm"
          :label="t('longTerm')"
          :disabled="longTermBlocked"
          @change="handleLongTermChange"
        />
      </div>
      <div
        v-if="locationsAndTimes.station && locationsAndTimes.station !== CUSTOM_LOCATIONS"
        class="flex h-8 items-center"
      >
        <Checkbox
          class="align-self-end"
          :model-value="locationsAndTimes.includeTransfers"
          :label="t('includeTransfers')"
          @change="handleIncludeTransfersChange"
        />
      </div>
    </div>

    <Locations
      v-if="locationsAndTimes.station === CUSTOM_LOCATIONS"
      :stations="stations"
      :pickup-location="pickupLocation"
      :dropoff-location="dropoffLocation"
      @change="handleLocationsChange"
    />

    <div class="grid grid-cols-2 items-end gap-4 md:grid-cols-3">
      <FilterDropdowns
        :category-filter="categoryFilter"
        :make-filter="makeFilter"
        class="col-span-2"
        @change="handleFiltersChange"
      />

      <CVButton
        v-if="hasChanged"
        variant="default"
        :disabled="!customLocationsComplete"
        class="max-sm:col-span-2"
        @click.prevent="updateProps"
        >{{ t('findCars') }}</CVButton
      >
    </div>
  </div>
  <div v-else class="flex items-center justify-center py-4"><Spinner /></div>
</template>

<script setup lang="ts">
import CVButton from '@/components/buttons/CVButton.vue';
import { CUSTOM_LOCATIONS, type CustomLocations } from '@/constants/custom-locations.constant';
import type { User } from '@/entities/auth/user.entity';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import type { BookingLocation } from '@/entities/bookings/booking.entity';
import type { Station } from '@/entities/station/station.entity';
import type { VehicleCategory } from '@/entities/vehicle-category.enum';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useFormHasChanged } from '@/hooks/use-form-has-changed';
import { useStations } from '@/queries/use-stations';
import type { InsuranceCaseProvider } from '@/views/bookings/booking-create/components/SelectInsuranceCaseProvider.vue';
import SelectInsuranceCaseProvider from '@/views/bookings/booking-create/components/SelectInsuranceCaseProvider.vue';
import { cloneDeep } from 'lodash';
import { computed, ref, watch, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterDropdowns from './FilterDropdowns.vue';
import Locations from './Locations.vue';
import SelectPartner from './SelectPartner.vue';
import StationAndTimesForm, { type StationAndTimes } from './StationAndTimesForm.vue';
import {
  isCustomerPaidPartner,
  isLongTermDurationTooShort,
  nextLongTermSearchValue,
} from '@/utils/long-term-create.utils';
import Checkbox from '@/components/Checkbox.vue';
export interface LocationsAndTimesProps {
  station: Station | CustomLocations | null;
  pickupLocation: BookingLocation | null;
  dropoffLocation: BookingLocation | null;
  startDate: Date;
  endDate: Date;
  categoryFilter: VehicleCategory | null;
  makeFilter: string | null;
  insuranceCaseProvider: InsuranceCaseProvider;
  partner?: User;
  longTerm: boolean;
  includeTransfers: boolean;
}

const props = defineProps<LocationsAndTimesProps>();

const emit = defineEmits<{
  (event: 'change', value: LocationsAndTimesProps): void;
  (event: 'changing-filter', value: boolean): void;
}>();

const { t } = useI18n();

const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);

const locationsAndTimes = ref(cloneDeep(props));
const initialLocationsAndTimes = ref(cloneDeep(props));
watchEffect(() => {
  locationsAndTimes.value = cloneDeep(props);
});
const showMore = ref(false);

const customLocationsComplete = computed(
  () =>
    ((locationsAndTimes.value.pickupLocation?.locationType === BookingLocationType.STATION &&
      locationsAndTimes.value.pickupLocation?.station) ||
      (locationsAndTimes.value.pickupLocation?.locationType === BookingLocationType.STREET &&
        locationsAndTimes.value.pickupLocation?.location)) &&
    ((locationsAndTimes.value.dropoffLocation?.locationType === BookingLocationType.STATION &&
      locationsAndTimes.value.dropoffLocation?.station) ||
      (locationsAndTimes.value.dropoffLocation?.locationType === BookingLocationType.STREET &&
        locationsAndTimes.value.dropoffLocation?.location)),
);

const longTermBlocked = computed(
  () =>
    !!locationsAndTimes.value.insuranceCaseProvider ||
    !isCustomerPaidPartner(locationsAndTimes.value.partner),
);

const longTermEligible = computed(
  () =>
    !isLongTermDurationTooShort(locationsAndTimes.value.startDate, locationsAndTimes.value.endDate),
);

const handleStationAndTimesChange = (stationAndTimes: StationAndTimes) => {
  const { station, startDate, endDate } = stationAndTimes;
  const pickupLocation =
    station !== CUSTOM_LOCATIONS
      ? {
          locationType: BookingLocationType.STATION,
          station,
          location: null,
        }
      : locationsAndTimes.value.pickupLocation;
  const dropoffLocation =
    station !== CUSTOM_LOCATIONS
      ? {
          locationType: BookingLocationType.STATION,
          station,
          location: null,
        }
      : locationsAndTimes.value.dropoffLocation;
  locationsAndTimes.value = {
    ...locationsAndTimes.value,
    station,
    pickupLocation,
    dropoffLocation,
    startDate,
    endDate,
    longTerm: nextLongTermSearchValue({
      eligible: !isLongTermDurationTooShort(startDate, endDate),
      wasEligible: longTermEligible.value,
      currentLongTerm: locationsAndTimes.value.longTerm,
      blocked: longTermBlocked.value,
    }),
  };
};

const handleLocationsChange = ({
  pickupLocation,
  dropoffLocation,
}: {
  pickupLocation: BookingLocation;
  dropoffLocation: BookingLocation;
}) => {
  locationsAndTimes.value = {
    ...locationsAndTimes.value,
    pickupLocation,
    dropoffLocation,
  };
};

const handleFiltersChange = (categoryFilter: VehicleCategory | null, makeFilter: string | null) => {
  locationsAndTimes.value = {
    ...locationsAndTimes.value,
    categoryFilter,
    makeFilter,
  };
};

const handleInsuranceCaseProviderChange = (insuranceCaseProvider: InsuranceCaseProvider) => {
  locationsAndTimes.value = {
    ...locationsAndTimes.value,
    insuranceCaseProvider,
    longTerm: insuranceCaseProvider ? false : locationsAndTimes.value.longTerm,
  };
};

const handlePartnerSelection = (partner?: User) => {
  locationsAndTimes.value = {
    ...locationsAndTimes.value,
    partner,
    longTerm: isCustomerPaidPartner(partner) ? locationsAndTimes.value.longTerm : false,
  };
};

const handleLongTermChange = (value: boolean) => {
  locationsAndTimes.value = {
    ...locationsAndTimes.value,
    longTerm: value && !longTermBlocked.value,
  };
};

const handleIncludeTransfersChange = (value: boolean) => {
  locationsAndTimes.value = {
    ...locationsAndTimes.value,
    includeTransfers: value,
  };
};

const updateProps = () => {
  emit('change', cloneDeep(locationsAndTimes.value));
  initialLocationsAndTimes.value = cloneDeep(locationsAndTimes.value);
};

const hasChanged = useFormHasChanged(initialLocationsAndTimes, locationsAndTimes);

watch(hasChanged, () => {
  emit('changing-filter', hasChanged.value);
});
</script>

<i18n lang="json">
{
  "en": {
    "findCars": "Find Cars",
    "showMore": "Insurance case / Business Customer",
    "longTerm": "Long-term rental",
    "includeTransfers": "Incl. transfers"
  },
  "de": {
    "findCars": "Autos finden",
    "showMore": "Versicherungsfall / Businesskunde",
    "longTerm": "Langzeitmiete",
    "includeTransfers": "Inkl. Transfers"
  },
  "es": {
    "findCars": "Buscar coches",
    "showMore": "Siniestro / cliente empresa",
    "longTerm": "Alquiler a largo plazo",
    "includeTransfers": "Incl. traslados"
  }
}
</i18n>
